<!--
 * @Description: 客户签约主体列表页
 * @Author: mdb
 * @Date: 2023-02-17 11:27:35
 * @LastEditTime: 2023-02-27 11:29:09
 * @LastEditors: mdb
-->
<template>
  <dc-layout-main :is-breadcrumb="true">
    <dc-layout-content>
      <cos-table
        serial
        :columns="columns"
        v-model:search="queryForm"
        @fetch="getList"
        @mask="showMask"
        ref="tableRef"
        tableId="DcCosFbuMainBody"
      >
        <template #search>
          <a-form :model="queryForm">
            <dc-form-item label="主客户代码">
              <a-input
                v-model:value.trim="queryForm.customer.customerNumber"
                :maxlength="200"
                allowClear
                placeholder="请输入"
              />
            </dc-form-item>
            <dc-form-item label="客户名称">
              <a-input
                v-model:value.trim="queryForm.customer.customerName"
                :maxlength="200"
                allowClear
                placeholder="请输入"
              />
            </dc-form-item>
            <dc-form-item label="客户ID">
              <a-input v-model:value.trim="queryForm.customer.cosId" :maxlength="200" allowClear placeholder="请输入" />
            </dc-form-item>
            <dc-form-item label="签约主体">
              <a-input v-model:value.trim="queryForm.signBodyName" :maxlength="200" allowClear placeholder="请输入" />
            </dc-form-item>
            <dc-form-item label="子客户代码">
              <a-input v-model:value.trim="queryForm.customerCode" :maxlength="200" allowClear placeholder="请输入" />
            </dc-form-item>
            <dc-form-item label="账户编号">
              <a-input v-model:value.trim="queryForm.accountCode" :maxlength="200" allowClear placeholder="请输入" />
            </dc-form-item>
            <dc-form-item label="组织部门">
              <CustomerAreaCascader type="search" appId="cos_fbu" v-model:ids="queryForm.customer.sellerRegionList" />
            </dc-form-item>
            <dc-form-item label="客户负责人">
              <UserSelect v-model:value="queryForm.customer.seller" placeholder="请选择" allowClear />
            </dc-form-item>
          </a-form>
        </template>

        <template #button>
          <ExportButton v-permission="'fbu_main_body_btn'" appId="cos_fbu" taskType="212" :search="queryForm"
            >导出</ExportButton
          >
        </template>

        <template #bodyCell="{ column, text }">
          <template v-if="column.dataIndex === 'signBodyBalanceList'">
            {{ text?.map((item: any) => `${item.currencyCode}：${item.quota}`).join('、') }}
          </template>
          <template v-if="column.key === 'sellerRegion'">
            <CustomerAreaCascaderText appId="cos_fbu" :value="text" />
          </template>
        </template>
      </cos-table>
    </dc-layout-content>
  </dc-layout-main>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onBeforeMount } from 'vue'
import { useRoute } from 'vue-router'
import CustomerAreaCascader from '@/components/CustomerAreaCascader/index.vue'
import CustomerAreaCascaderText from '@/components/CustomerAreaCascader/CustomerAreaCascaderText.vue'
import { mainBodyList, mainBodyDetail } from '@/api/gucangSales/customerCenter'
import type { MainBodyParams } from '@/typings/api/customerCenter'
import { mainBodyStatusEnum } from '@/views/gucangSales/commonEnum/dataConfig'

const tableRef = ref()
const queryForm = reactive<MainBodyParams>({ customer: {} })
const columns = computed(() => [
  { title: '主客户代码', dataIndex: ['customer', 'customerNumber'] },
  { title: '客户名称', dataIndex: ['customer', 'customerName'] },
  { title: '客户ID', dataIndex: ['customer', 'cosId'] },
  { title: '子客户代码', dataIndex: 'customerCode' },
  { title: '账户编号', dataIndex: 'accountCode', headerQuestion: '取值来源：ark签约主体管理-账户编号' },
  { title: '签约主体', dataIndex: 'signBodyName', headerQuestion: '取值来源：ark签约主体管理-主体名称' },
  { title: '服务主体', dataIndex: 'serviceBodyName', headerQuestion: '取值来源：ark签约主体管理-服务主体' },
  {
    title: '主体联系人',
    dataIndex: 'contactName',
    cosType: 'mask',
    headerQuestion: '取值来源：ark签约主体管理-联系人姓名',
    minWidth: 130
  },
  {
    title: '主体手机号码',
    dataIndex: 'phoneNumber',
    cosType: 'mask',
    headerQuestion: '取值来源：ark签约主体管理-联系电话',
    width: 145
  },
  {
    title: '可用余额',
    dataIndex: 'signBodyBalanceList',
    headerQuestion: '取值来源：ark签约主体管理-可用余额',
    width: 200
  },
  { title: '信用额度', dataIndex: 'totalQuota', headerQuestion: '取值来源：ark签约主体管理-信用额度' },
  { title: '总可用额度', dataIndex: 'allTotal', headerQuestion: '取值来源：ark签约主体管理-总可用额度', minWidth: 130 },
  { title: '出账周期', dataIndex: 'accountCycleTypeStr', headerQuestion: '取值来源：ark签约主体管理-出账周期' },
  { title: '对账周期', dataIndex: 'reconciliationCycleTypeStr', headerQuestion: '取值来源：ark签约主体管理-对账周期' },
  { title: '签约业务', dataIndex: 'signBusinessTypeStr', headerQuestion: '取值来源：ark签约主体管理-签约业务' },
  {
    title: '已签约业务',
    dataIndex: 'contractedBusinessStr',
    headerQuestion: '取值来源：ark签约主体管理-已签约业务',
    minWidth: 130
  },
  {
    title: '注册国家/区域',
    dataIndex: 'registerAreaCode',
    headerQuestion: '取值来源：ark签约主体管理-注册国家/区域',
    width: 160
  },
  {
    title: '主体类别',
    dataIndex: 'type',
    dicData: [
      { value: 'C101', label: '个人' },
      { value: 'C102', label: '公司' }
    ],
    headerQuestion: '取值来源：ark签约主体管理-主体类别'
  },
  {
    title: '状态',
    dataIndex: 'signBodyStatus',
    headerQuestion: '取值来源：ark签约主体管理-状态',
    dicData: mainBodyStatusEnum
  },
  { title: '审核时间', dataIndex: 'examineTime', headerQuestion: '取值来源：ark签约主体管理-审核时间', width: 190 },
  {
    title: '客户负责人',
    dataIndex: ['customer', 'sellerName'],
    headerQuestion: '取值来源：ark签约主体管理-销售代表',
    minWidth: 130
  },
  { title: '负责人区域', dataIndex: ['customer', 'sellerRegion'], key: 'sellerRegion', minWidth: 190 }
])

// 检测 URL 是否带有查询参数，有则使用
const route = useRoute()
const queryCustomerCode = computed(() => route.query.customerCode as string)
onBeforeMount(() => {
  if (queryCustomerCode.value) queryForm.customerCode = queryCustomerCode.value
})

const getList = (queryForm: MainBodyParams, callBack: (res: any) => void) => {
  mainBodyList(queryForm).then((res) => {
    callBack(res)
  })
}

// 获取详情信息
const showMask = (record: any, callBack: (res: any) => void) => {
  mainBodyDetail({ id: record.id }).then((res) => {
    callBack(res.data)
  })
}
</script>
<script lang="ts">
export default { name: 'DcCosFbuMainBody' }
</script>

<style lang="scss" scoped></style>
